// src/services/apiClient.js - Extended with location management
import axios from 'axios';
import * as indexedDB from '../utils/indexedDB';
import * as tokenEncryption from '../utils/tokenEncryption';
import { handleSessionExpiry } from '../utils/sessionRecovery';

// Get base URL from environment or use default
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 3600000, // 1 hour timeout for large file uploads (up to 20GB)
  withCredentials: true,
});

// Create a public axios instance WITHOUT auth interceptors
// Used for endpoints that don't require authentication (e.g., public alarm access)
const publicAxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000,
  withCredentials: false,
});

// Track if we're currently refreshing to avoid multiple refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Helper function to get token from storage
const getTokenFromStorage = async (key) => {
  try {
    // Try IndexedDB first
    if (indexedDB.isAvailable()) {
      const encryptedToken = await indexedDB.getItem(key);

      if (encryptedToken) {
        try {
          const decryptedToken = await tokenEncryption.decryptToken(encryptedToken);
          if (decryptedToken) {
            return decryptedToken;
          }
        } catch (decryptError) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Decryption failed:', decryptError);
          }
        }
      }
    }

    // Fallback to localStorage
    const localToken = localStorage.getItem(key);
    if (localToken) {
      return localToken;
    }

    return null;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error retrieving token:', error);
    }
    // Last resort
    return localStorage.getItem(key);
  }
};

// Helper function to set token in storage
const setTokenInStorage = async (key, token) => {
  if (indexedDB.isAvailable()) {
    const encryptedToken = await tokenEncryption.encryptToken(token);
    await indexedDB.setItem(key, encryptedToken);
  } else {
    localStorage.setItem(key, token);
  }
};

// Add request interceptor to include auth token
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await getTokenFromStorage('auth_token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('Request interceptor error:', error);
    }
    return Promise.reject(error);
  }
);

// Add response interceptor to handle common errors and token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle authentication errors
    if (error.response && error.response.status === 401) {
      // Don't try to refresh on these endpoints
      const noRefreshPaths = ['/auth/refresh', '/auth/login', '/auth/microsoft'];
      const isNoRefreshPath = noRefreshPaths.some(path => originalRequest.url?.includes(path));

      // Try to refresh the token if we haven't already tried
      if (!originalRequest._retry && !isNoRefreshPath) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers['Authorization'] = 'Bearer ' + token;
              return axiosInstance(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Get refresh token from storage
          const refreshToken = await getTokenFromStorage('refresh_token');

          if (refreshToken) {
            // Attempt to refresh the token
            const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
              refreshToken
            });

            if (response.data.success && response.data.token) {
              const newAccessToken = response.data.token;
              const newRefreshToken = response.data.refreshToken || refreshToken;

              // Store new tokens
              await setTokenInStorage('auth_token', newAccessToken);
              await setTokenInStorage('refresh_token', newRefreshToken);

              // Update the failed request with new token
              originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;

              processQueue(null, newAccessToken);
              isRefreshing = false;

              // Retry the original request
              return axiosInstance(originalRequest);
            }
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          isRefreshing = false;

          if (process.env.NODE_ENV === 'development') {
            console.error('Token refresh failed:', refreshError);
          }

          // Use automatic session recovery
          await handleSessionExpiry('token_refresh_failed');

          return Promise.reject(refreshError);
        }
      }

      // No refresh token or refresh already attempted
      // Use automatic session recovery
      await handleSessionExpiry('session_expired');
    }
    return Promise.reject(error);
  }
);

// API client for authentication
export const authApi = {
  // Standard authentication methods
  login: (credentials) => axiosInstance.post('/auth/login', credentials),
  register: (userData) => axiosInstance.post('/auth/register', userData),
  getCurrentUser: () => axiosInstance.get('/auth/me'),
  changePassword: (passwordData) => axiosInstance.put('/auth/change-password', passwordData),
  logout: (refreshToken) => axiosInstance.post('/auth/logout', { refreshToken }),
  sessionCheck: () => axiosInstance.get('/auth/me', { timeout: 5000 }),

  // Refresh token
  refreshToken: (refreshToken) => axiosInstance.post('/auth/refresh', { refreshToken }),

  // Get user devices
  getDevices: () => axiosInstance.get('/auth/devices'),

  // Revoke device
  revokeDevice: (fingerprint) => axiosInstance.delete(`/auth/devices/${fingerprint}`),
  
  // Microsoft authentication method
  microsoftLogin: (redirectTo = '/dashboard') => {
    // The frontend URL (localhost in development, actual domain in production)
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'https://portal.caro.care';
    
    // Add a callbackUrl parameter to tell the API where to redirect after auth
    const callbackUrl = encodeURIComponent(`${frontendUrl}/auth-callback`);
    
    // Redirect to the backend Microsoft authentication endpoint
    const authUrl = `${API_BASE_URL}/api/auth/microsoft?callbackUrl=${callbackUrl}&redirectTo=${encodeURIComponent(redirectTo)}`;
    window.location.href = authUrl;
    return Promise.resolve({ success: true });
  },
  
  // Handle authentication callback with token
  handleAuthCallback: async (token) => {
    if (!token) {
      return { success: false, message: 'No token provided' };
    }
    
    try {
      // Store the token
      localStorage.setItem('auth_token', token);
      
      // Fetch the user data
      const response = await axiosInstance.get('/auth/me');
      
      if (response.data.success && response.data.user) {
        return { 
          success: true, 
          user: response.data.user 
        };
      } else {
        throw new Error('Invalid user data received');
      }
    } catch (err) {
      // Clean up if there's an error
      localStorage.removeItem('auth_token');
      return { 
        success: false, 
        message: err.response?.data?.message || err.message || 'Authentication failed' 
      };
    }
  }
};

// API client for tickets
export const ticketsApi = {
  getAll: (params) => axiosInstance.get('/tickets', { params }),
  getById: (id) => axiosInstance.get(`/tickets/${id}`),
  create: (ticketData) => axiosInstance.post('/tickets', ticketData),
  update: (id, ticketData) => axiosInstance.put(`/tickets/${id}`, ticketData),
  delete: (id) => axiosInstance.delete(`/tickets/${id}`),
  addComment: (id, commentData) => axiosInstance.post(`/tickets/${id}/messages`, commentData),
  addAttachment: (id, formData) => axiosInstance.post(`/tickets/${id}/attachments`, formData),
  deleteAttachment: (ticketId, attachmentId) => axiosInstance.delete(`/tickets/${ticketId}/attachments/${attachmentId}`),
  assignTicket: (id, userId) => axiosInstance.post(`/tickets/${id}/assign`, { userId }),
  changeStatus: (id, status) => axiosInstance.post(`/tickets/${id}/status`, { status }),
  getStatistics: () => axiosInstance.get('/tickets/statistics'),

  // Follow/Unfollow tickets
  followTicket: (id) => axiosInstance.post(`/tickets/${id}/follow`),
  follow: (id) => axiosInstance.post(`/tickets/${id}/follow`),
  unfollowTicket: (id) => axiosInstance.delete(`/tickets/${id}/follow`),
  unfollow: (id) => axiosInstance.delete(`/tickets/${id}/follow`),
  getFollowers: (id) => axiosInstance.get(`/tickets/${id}/followers`),

  // Known issues and solutions
  createKnownIssue: (issueData) => axiosInstance.post('/known-issues', issueData),
  getKnownIssues: (params) => axiosInstance.get('/known-issues', { params }),
  getKnownIssueById: (id) => axiosInstance.get(`/known-issues/${id}`),
  updateKnownIssue: (id, issueData) => axiosInstance.put(`/known-issues/${id}`, issueData),
  deleteKnownIssue: (id) => axiosInstance.delete(`/known-issues/${id}`),

  createSolution: (solutionData) => axiosInstance.post('/solutions', solutionData),
  getSolutions: (params) => axiosInstance.get('/solutions', { params }),
  getSolutionById: (id) => axiosInstance.get(`/solutions/${id}`),
  updateSolution: (id, solutionData) => axiosInstance.put(`/solutions/${id}`, solutionData),
  deleteSolution: (id) => axiosInstance.delete(`/solutions/${id}`),

  // Apply solution to a ticket
  applySolution: (id, data) => axiosInstance.post(`/tickets/${id}/solutions`, data),

  // Get interventions for a ticket (uses field-interventions endpoint matching old working code)
  getInterventions: (ticketId) => axiosInstance.get('/field-interventions', { params: { ticket_id: ticketId } }),

  // Request physical intervention
  requestPhysicalIntervention: (id, data) => axiosInstance.post(`/tickets/${id}/request-intervention`, data),
  requestIntervention: (id, data) => axiosInstance.post(`/tickets/${id}/request-intervention`, data),

  // Reschedule intervention
  rescheduleIntervention: (ticketId, interventionId, data) => axiosInstance.put(`/tickets/${ticketId}/interventions/${interventionId}/reschedule`, data),

  // Cancel intervention
  cancelIntervention: (ticketId, interventionId, data) => axiosInstance.put(`/tickets/${ticketId}/interventions/${interventionId}/cancel`, data),

  // Get tickets by location
  getByLocation: (locationId) => axiosInstance.get(`/tickets`, { params: { locationId } }),
};

// API client for locations
export const locationsApi = {
  // Get all locations with optional filters
  getAll: (params) => axiosInstance.get('/locations', {
    params: { limit: 5000, ...params }
  }),

  // Get specific location by ID
  getById: (id) => axiosInstance.get(`/locations/${id}`),

  // Get locations by type (site, building, floor, unit, room, etc.)
  getByType: (type) => axiosInstance.get(`/locations/type/${type}`),

  // Get locations by parent ID (children of a specific location)
  getByParent: (parentId) => axiosInstance.get(`/locations/${parentId}/children`),

  // Get full location hierarchy (returns complete tree structure)
  getHierarchy: () => axiosInstance.get('/locations/hierarchy'),

  // Create new location
  create: (locationData) => axiosInstance.post('/locations', locationData),

  // Update existing location
  update: (id, locationData) => axiosInstance.put(`/locations/${id}`, locationData),

  // Delete location
  delete: (id) => axiosInstance.delete(`/locations/${id}`),

  // Get devices connected to a location
  getDevices: (locationId) => axiosInstance.get(`/locations/${locationId}/devices`),

  // Get user's accessible locations
  getUserAccessibleLocations: (locationType, parentId = null) => {
    const params = {};
    if (parentId) params.parentId = parentId;
    return axiosInstance.get(`/locations/accessible/${locationType}`, { params });
  },

  // Get statistics about locations
  getStatistics: () => axiosInstance.get('/locations/statistics'),

  // Get location ancestors (parent chain up to root)
  getAncestors: (locationId) => axiosInstance.get(`/locations/${locationId}/ancestors`),

  // Search locations by name or properties
  search: (query) => axiosInstance.get('/locations/search', { params: { query } }),

  // Manage location access
  grantAccess: (locationId, userId) => axiosInstance.post(`/locations/${locationId}/access`, { userId }),
  revokeAccess: (locationId, userId) => axiosInstance.delete(`/locations/${locationId}/access/${userId}`),

  // Get sites accessible to current user (for site filter dropdown)
  getSites: () => axiosInstance.get('/locations/sites'),

  // Get all location IDs within a site (for filtering)
  getSiteLocationIds: (siteId) => axiosInstance.get(`/locations/sites/${siteId}/location-ids`),
};

// API client for location types
export const locationTypesApi = {
  // Get all location types
  getAll: () => axiosInstance.get('/locationtypes'),

  // Get location type by ID
  getById: (id) => axiosInstance.get(`/locationtypes/${id}`),
};

// API client for devices
export const devicesApi = {
  // Get all devices
  getAll: (params) => axiosInstance.get('/devices', {
    params: { limit: 5000, ...params }
  }),

  // Get device by ID
  getById: (id) => axiosInstance.get(`/devices/${id}`),

  // Get devices by location
  getByLocation: (locationId) => axiosInstance.get(`/devices/location/${locationId}`, {
    params: { limit: 5000 }
  }),

  // Create new device
  create: (deviceData) => axiosInstance.post('/devices', deviceData),

  // Update existing device
  update: (id, deviceData) => axiosInstance.put(`/devices/${id}`, deviceData),

  // Delete device
  delete: (id) => axiosInstance.delete(`/devices/${id}`),

  // Ping single device to check if it's working
  ping: (id) => axiosInstance.post(`/devices/${id}/ping`),

  // Get device status and history
  getStatus: (id) => axiosInstance.get(`/devices/${id}/status`),

  // Ping multiple devices at once
  pingBatch: (deviceIds) => axiosInstance.post('/devices/ping/batch', { deviceIds }),

  // Get ping history for a device
  getPingHistory: (id, params) => {
    return axiosInstance.get(`/devices/${id}/ping-history`, { params });
  },

  // Get ping statistics for charts
  getPingStats: (id, params) => {
    return axiosInstance.get(`/devices/${id}/ping-stats`, { params });
  },

  // Restart device
  restart: (id) => axiosInstance.post(`/devices/${id}/restart`),

  // Update device firmware
  updateFirmware: (id) => axiosInstance.post(`/devices/${id}/update-firmware`),

  // Get device logs
  getLogs: (id, params) => axiosInstance.get(`/devices/${id}/logs`, { params }),

  // Search devices
  search: (query) => axiosInstance.get('/devices/search', { params: { query } }),

  // SSH connection endpoint
  getSSHConfig: (id) => axiosInstance.get(`/devices/${id}/ssh-config`),

  // Test SSH connectivity
  testSSH: (id, params) => axiosInstance.post(`/devices/${id}/test-ssh`, params),

  // Get device types
  getDeviceTypes: (params) => axiosInstance.get('/devicetypes', { params }),

  // Uninstall a service from a device
  uninstallService: (deviceId, data) => axiosInstance.post(`/devices/${deviceId}/services/uninstall`, data),

  // Device commands
  killDevice: (deviceId) => axiosInstance.post(`/devices/${deviceId}/commands/kill`),
  uninstallAllServices: (deviceId) => axiosInstance.post(`/devices/${deviceId}/commands/uninstall`),
  updateServices: (deviceId, serviceName = null, version = null) => {
    const params = {};
    if (serviceName) params.serviceName = serviceName;
    if (version) params.version = version;
    return axiosInstance.post(`/devices/${deviceId}/commands/update`, params);
  },
  forceRegister: (deviceId) => axiosInstance.post(`/devices/${deviceId}/commands/register`),

  // Bulk actions on multiple devices
  bulkAction: (actionData) => axiosInstance.post('/devices/bulk-action', actionData),

  // Get monitoring history for a device
  getMonitoringHistory: (id, params) => axiosInstance.get(`/devices/${id}/monitoring-history`, { params }),
};

// API client for notifications
export const notificationsApi = {
  getAll: (params) => axiosInstance.get('/notifications', { params }),
  markAsRead: (id) => axiosInstance.put(`/notifications/${id}/read`),
  markAllAsRead: () => axiosInstance.put('/notifications/mark-all-read'),
  getUnreadCount: () => axiosInstance.get('/notifications/unread-count'),
  subscribe: (subscription) => axiosInstance.post('/notifications/subscribe', subscription),
  unsubscribe: (subscriptionId) => axiosInstance.delete(`/notifications/subscriptions/${subscriptionId}`),

  // Admin functions
  sendNotification: (notificationData) => axiosInstance.post('/notifications/admin/send', notificationData),
  getAllSentNotifications: (params) => axiosInstance.get('/notifications/admin/sent', { params }),
  getNotificationById: (id) => axiosInstance.get(`/notifications/admin/${id}`),
  updateNotification: (id, notificationData) => axiosInstance.put(`/notifications/admin/${id}`, notificationData),
  deleteNotification: (id) => axiosInstance.delete(`/notifications/admin/${id}`),
};

// API client for users
export const usersApi = {
  getAll: () => axiosInstance.get('/users'),
  getById: (id) => axiosInstance.get(`/users/${id}`),
  update: (id, userData) => axiosInstance.put(`/users/${id}`, userData),
  updateProfile: (userData) => axiosInstance.put('/users/profile', userData),
  updatePreferences: (preferences) => axiosInstance.put('/users/preferences', preferences),

  // Permission management
  getCompanyUsers: () => axiosInstance.get('/permissions/company-users'),
  getMyPermissions: () => axiosInstance.get('/permissions/me'),
  getUserPermissions: (userId) => axiosInstance.get(`/permissions/user/${userId}`),
  updateUserPermissions: (userId, permissions) => axiosInstance.put(`/permissions/user/${userId}`, permissions),
  deleteUser: (userId) => axiosInstance.delete(`/users/${userId}`),
};

// API client for logs
export const logsApi = {
  getLogs: (params) => axiosInstance.get('/logs', { params }),
  getByLocation: (locationId, params) => axiosInstance.get(`/logs/location/${locationId}`, { params }),
  getByDevice: (deviceId, params) => axiosInstance.get(`/logs/device/${deviceId}`, { params }),
  getByUser: (userId, params) => axiosInstance.get(`/logs/user/${userId}`, { params }),
  getByTicket: (ticketId, params) => axiosInstance.get(`/logs/ticket/${ticketId}`, { params }),
  exportLogs: (params) => axiosInstance.get('/logs/export', { params, responseType: 'blob' }),
  getStats: () => axiosInstance.get('/logs/stats'),
  clearLogs: () => axiosInstance.post('/logs/clear')
};

// API client for camera footage requests
export const cameraFootageRequestsApi = {
  // Get all camera footage requests with filtering
  getAll: (params) => axiosInstance.get('/camera-footage-requests', { params }),

  // Get single camera footage request by ID
  getById: (id) => axiosInstance.get(`/camera-footage-requests/${id}`),

  // Get request timeline/audit log
  getTimeline: (id) => axiosInstance.get(`/camera-footage-requests/${id}/timeline`),

  // Create new camera footage request
  create: (requestData) => axiosInstance.post('/camera-footage-requests', requestData),

  // Site manager approval
  siteApprove: (id, data) => axiosInstance.post(`/camera-footage-requests/${id}/site-approve`, data),

  // Site manager rejection
  siteReject: (id, data) => axiosInstance.post(`/camera-footage-requests/${id}/site-reject`, data),

  // DPO approval
  dpoApprove: (id, data) => axiosInstance.post(`/camera-footage-requests/${id}/dpo-approve`, data),

  // DPO rejection
  dpoReject: (id, data) => axiosInstance.post(`/camera-footage-requests/${id}/dpo-reject`, data),

  // Support adds footage
  addFootage: (id, data) => axiosInstance.post(`/camera-footage-requests/${id}/add-footage`, data),

  // Delete camera footage request (admin only)
  delete: (id) => axiosInstance.delete(`/camera-footage-requests/${id}`),

  // Export audit report as PDF
  exportPDF: () => axiosInstance.get('/camera-footage-requests/export/pdf', {
    responseType: 'blob'
  }),
};

// API client for documentation
export const documentationApi = {
  // Get documentation list
  getList: () => axiosInstance.get('/documentation/list'),

  // Get single documentation by slug
  getBySlug: (slug) => axiosInstance.get(`/documentation/${slug}`),
};

// API client for files
export const filesApi = {
  // Upload file
  upload: (formData) => axiosInstance.post('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Delete file
  delete: (fileId) => axiosInstance.delete(`/files/${fileId}`),
};

// API client for companies
export const companiesApi = {
  // Get all companies (admin only)
  getAll: (params) => axiosInstance.get('/companies', { params }),

  // Get company by ID (admin only)
  getById: (id) => axiosInstance.get(`/companies/${id}`),

  // Create new company (admin only)
  create: (companyData) => axiosInstance.post('/companies', companyData),

  // Update company (admin only)
  update: (id, companyData) => axiosInstance.put(`/companies/${id}`, companyData),

  // Delete company (admin only)
  delete: (id) => axiosInstance.delete(`/companies/${id}`),

  // Get company users (admin only)
  getUsers: (id, params) => axiosInstance.get(`/companies/${id}/users`, { params }),

  // Get company locations (admin only)
  getLocations: (id) => axiosInstance.get(`/companies/${id}/locations`),

  // Get company statistics (admin only)
  getStatistics: (id) => axiosInstance.get(`/companies/${id}/statistics`),

  // Get support types for current user's company (all users)
  getSupportTypes: () => axiosInstance.get('/companies/support-types'),
};

// API client for field interventions
export const fieldInterventionsApi = {
  // Get all field intervention reports with filtering and pagination
  getAll: (params) => axiosInstance.get('/field-interventions', { params }),

  // Get initialization data for new intervention
  getNew: () => axiosInstance.get('/field-interventions/new'),

  // Get single field intervention report by ID
  getById: (id) => axiosInstance.get(`/field-interventions/${id}`),

  // Create new field intervention report (manually)
  create: (interventionData) => axiosInstance.post('/field-interventions', interventionData),

  // Create field intervention from existing ticket
  createFromTicket: (ticketId) => axiosInstance.post(`/field-interventions/from-ticket/${ticketId}`),

  // Update field intervention report
  update: (id, interventionData) => axiosInstance.put(`/field-interventions/${id}`, interventionData),

  // Close field intervention report with outcome
  close: (id, outcomeData) => axiosInstance.post(`/field-interventions/${id}/close`, outcomeData),

  // Mark field intervention as invoiced
  markAsInvoiced: (id, invoicedData = { invoiced: true }) => axiosInstance.post(`/field-interventions/${id}/invoice`, invoicedData),

  // Generate PDF report for intervention
  getPDF: (id) => axiosInstance.get(`/field-interventions/${id}/pdf`, {
    responseType: 'blob',
    timeout: 60000 // PDF generation might take longer
  }),

  // Delete field intervention report (admin only)
  delete: (id) => axiosInstance.delete(`/field-interventions/${id}`),
};

// API client for Hexnode MDM (Mobile Device Management)
export const hexnodeApi = {
  // Device Management
  getDevices: () => axiosInstance.get('/hexnode/devices'),
  getDevice: (deviceId) => axiosInstance.get(`/hexnode/devices/${deviceId}`),
  syncDevice: (deviceId, hexnodeDeviceId) => axiosInstance.post(`/hexnode/sync/${deviceId}`, { hexnodeDeviceId }),
  importAll: () => axiosInstance.post('/hexnode/import'),
  getImportStatus: () => axiosInstance.get('/hexnode/import/status'),

  // Device Actions
  restartDevice: (deviceId) => axiosInstance.post(`/hexnode/devices/${deviceId}/actions/restart`),
  clearAppData: (deviceId, appIdentifier) => axiosInstance.post(`/hexnode/devices/${deviceId}/actions/clear-data`, { appIdentifier }),
  uninstallApp: (deviceId, appId) => axiosInstance.post(`/hexnode/devices/${deviceId}/actions/uninstall`, { appId }),
  ringDevice: (deviceId, message) => axiosInstance.post(`/hexnode/devices/${deviceId}/actions/ring`, { message }),
  broadcastMessage: (deviceId, message) => axiosInstance.post(`/hexnode/devices/${deviceId}/actions/broadcast`, { message }),

  // Device Information
  getApplications: (deviceId) => axiosInstance.get(`/hexnode/devices/${deviceId}/applications`),
  getCaroApp: (deviceId) => axiosInstance.get(`/hexnode/devices/${deviceId}/caro-app`),
  getRemainingCalls: (deviceId) => axiosInstance.get(`/hexnode/devices/${deviceId}/remaining-calls`),
  getLogs: (deviceId, params) => axiosInstance.get(`/hexnode/devices/${deviceId}/logs`, { params }),
};

// API client for UniFi Network Controller
export const unifiApi = {
  // Sites Management
  getSites: () => axiosInstance.get('/unifi/sites'),

  // Device Management
  getDevices: (params) => axiosInstance.get('/unifi/devices', { params }),
  getDeviceDetails: (deviceId) => axiosInstance.get(`/unifi/devices/${deviceId}`),
  syncDeviceStatus: (deviceId) => axiosInstance.post(`/unifi/sync/${deviceId}`),

  // Import
  importDevices: (data) => axiosInstance.post('/unifi/import', data),
  getImportStatus: () => axiosInstance.get('/unifi/import/status'),

  // Device Actions
  restartDevice: (deviceId) => axiosInstance.post(`/unifi/devices/${deviceId}/restart`),
  forceProvision: (deviceId) => axiosInstance.post(`/unifi/devices/${deviceId}/provision`),
  upgradeDevice: (deviceId) => axiosInstance.post(`/unifi/devices/${deviceId}/upgrade`),

  // Switch Specific
  getSwitchPorts: (deviceId) => axiosInstance.get(`/unifi/devices/${deviceId}/ports`),

  // PoE Port Control (SSH-based)
  disablePoEPort: (deviceId, portIdx) => axiosInstance.post(`/unifi/devices/${deviceId}/ports/${portIdx}/poe/disable`),
  enablePoEPort: (deviceId, portIdx) => axiosInstance.post(`/unifi/devices/${deviceId}/ports/${portIdx}/poe/enable`),
  powerCyclePoEPort: (deviceId, portIdx, delaySeconds = 60) =>
    axiosInstance.post(`/unifi/devices/${deviceId}/ports/${portIdx}/poe/power-cycle`, { delaySeconds }),

  // Network Topology
  getNetworkTopology: (site) => axiosInstance.get('/unifi/topology', { params: { site } }),

  // Clients
  getClients: (params) => axiosInstance.get('/unifi/clients', { params }),

  // Logs
  getLogs: (params) => axiosInstance.get('/unifi/logs', { params }),
};

// API client for Clearvox VoIP Integration
export const clearvoxApi = {
  // Site Discovery
  discoverSites: () => axiosInstance.get('/admin/clearvox/discover-sites'),
  getSites: () => axiosInstance.get('/admin/clearvox/sites'),
  testConnection: (siteId) => axiosInstance.post(`/admin/clearvox/test-connection/${siteId}`),

  // Import & Sync
  importAll: () => axiosInstance.post('/admin/clearvox/import'),
  importSite: (siteId) => axiosInstance.post(`/admin/clearvox/import/${siteId}`),
  syncAll: () => axiosInstance.post('/admin/clearvox/sync'),
  syncSite: (siteId) => axiosInstance.post(`/admin/clearvox/sync/${siteId}`),
  cancelSync: (siteId) => axiosInstance.post(`/admin/clearvox/cancel-sync/${siteId}`),
  getSyncStatus: () => axiosInstance.get('/admin/clearvox/sync-status'),
  getSyncStatusBySite: (siteId) => axiosInstance.get(`/admin/clearvox/sync-status/${siteId}`),

  // Statistics
  getStats: () => axiosInstance.get('/admin/clearvox/stats'),

  // Clearvox Users
  getUsers: (params) => axiosInstance.get('/clearvox/users', { params }),
  getUserById: (id) => axiosInstance.get(`/clearvox/users/${id}`),
  getUserByInternalNumber: (number) => axiosInstance.get(`/clearvox/users/by-internal/${number}`),
  getUserByExternalNumber: (number) => axiosInstance.get(`/clearvox/users/by-external/${number}`),
  linkUserToLocation: (id, locationId) => axiosInstance.post(`/clearvox/users/${id}/link-location`, { locationId }),
  unlinkUserFromLocation: (id) => axiosInstance.delete(`/clearvox/users/${id}/unlink-location`),
  syncUser: (id) => axiosInstance.post(`/clearvox/users/${id}/sync`),

  // Clearvox Devices
  getDevices: (params) => axiosInstance.get('/clearvox/devices', { params }),
  getDeviceById: (id) => axiosInstance.get(`/clearvox/devices/${id}`),
  getDeviceByUser: (userId) => axiosInstance.get(`/clearvox/devices/by-user/${userId}`),

  // Call History
  getCallHistory: (userId, params) => axiosInstance.get(`/clearvox/users/${userId}/call-history`, { params }),
  searchCallHistory: (params) => axiosInstance.get('/clearvox/call-history/search', { params }),

  // Location Linking
  getLocationUser: (locationId) => axiosInstance.get(`/clearvox/locations/${locationId}/user`),
  bulkLinkSuggestions: (data) => axiosInstance.post('/clearvox/locations/bulk-link', data),
  getUnlinkedUsers: (params) => axiosInstance.get('/clearvox/locations/unlinked-users', { params }),

  // Sync Logs
  getSyncLogs: (params) => axiosInstance.get('/admin/clearvox/sync-logs', { params }),
  getSyncLogById: (id) => axiosInstance.get(`/admin/clearvox/sync-logs/${id}`),

  // Settings
  getSettings: () => axiosInstance.get('/admin/clearvox/settings'),
  updateSettings: (settings) => axiosInstance.put('/admin/clearvox/settings', settings),
};

// API client for BaseIP Management
export const baseIPApi = {
  // Get all sites with baseIP statistics
  getSites: () => axiosInstance.get('/admin/baseip/sites'),

  // Get all units for a site with baseIP status
  getUnitsForSite: (siteId) => axiosInstance.get(`/admin/baseip/sites/${siteId}/units`),

  // Update baseIP for a single location (force=true to allow duplicates)
  updateBaseIP: (locationId, baseIP, force = false) => axiosInstance.put(`/admin/baseip/locations/${locationId}`, { baseIP, force }),

  // Bulk assign baseIPs to multiple units
  bulkAssign: (siteId, assignments) => axiosInstance.post(`/admin/baseip/sites/${siteId}/bulk`, { assignments }),

  // Get audit report
  getAuditReport: () => axiosInstance.get('/admin/baseip/audit'),
};

// API client for Telephony Management (Call Logs + SIP Sessions)
export const telephonyApi = {
  getCallLogs: (params) => axiosInstance.get('/call-logs', { params }),
  getCallLogAudioUrl: (id, type) => `${API_BASE_URL}/api/call-logs/${id}/audio${type ? `?type=${type}` : ''}`,
  getCallLogAudio: (id, type) => axiosInstance.get(`/call-logs/${id}/audio${type ? `?type=${type}` : ''}`, { responseType: 'blob' }),
  getSipSessions: (params) => axiosInstance.get('/sip-sessions', { params }),
  getSipSessionHistory: (id, params) => axiosInstance.get(`/sip-sessions/${id}/history`, { params }),
  // SIP device endpoints (2N, Grandstream, physical SIP devices)
  getSipEndpoints: (params) => axiosInstance.get('/sip-endpoints', { params }),
  getSipEndpoint: (id) => axiosInstance.get(`/sip-endpoints/${id}`),
  createSipEndpoint: (data) => axiosInstance.post('/sip-endpoints', data),
  updateSipEndpoint: (id, data) => axiosInstance.put(`/sip-endpoints/${id}`, data),
  deleteSipEndpoint: (id) => axiosInstance.delete(`/sip-endpoints/${id}`),
  regenerateSipCredentials: (id) => axiosInstance.post(`/sip-endpoints/${id}/regenerate-credentials`),
  getSipEndpointStatus: (id) => axiosInstance.get(`/sip-endpoints/${id}/status`),
  reloadAsterisk: () => axiosInstance.post('/sip-endpoints/reload-asterisk'),
  provisionLocationSipDevice: (data) => axiosInstance.post('/sip-endpoints/provision-location', data),
  provisionPersonSipDevice: (data) => axiosInstance.post('/sip-endpoints/provision-person', data),
  syncCameraPaths: () => axiosInstance.post('/sip-endpoints/sync-camera-paths'),
  // Audio device configuration
  getAudioConfig: (id) => axiosInstance.get(`/sip-endpoints/${id}/audio`),
  updateAudioConfig: (id, config) => axiosInstance.put(`/sip-endpoints/${id}/audio`, config),
  refreshAudioDevices: (id) => axiosInstance.post(`/sip-endpoints/${id}/audio/refresh`),
};

// API client for DID Mappings (external phone number → internal extension routing)
export const didMappingApi = {
  getAll: (params) => axiosInstance.get('/didMappings', { params }),
  getById: (id) => axiosInstance.get(`/didMappings/${id}`),
  create: (data) => axiosInstance.post('/didMappings', data),
  update: (id, data) => axiosInstance.put(`/didMappings/${id}`, data),
  delete: (id) => axiosInstance.delete(`/didMappings/${id}`),
};

// API client for persons
export const personsApi = {
  // Get all persons
  getAll: (params) => axiosInstance.get('/persons', {
    params: { limit: 5000, ...params }
  }),

  // Alias for getAll
  getPersons: (params) => axiosInstance.get('/persons', {
    params: { limit: 5000, ...params }
  }),

  // Get specific person by ID
  getById: (id) => axiosInstance.get(`/persons/${id}`),

  // Create new person
  create: (personData) => axiosInstance.post('/persons', personData),

  // Update existing person
  update: (id, personData) => axiosInstance.put(`/persons/${id}`, personData),

  // Delete person
  delete: (id) => axiosInstance.delete(`/persons/${id}`),

  // Bulk delete persons
  bulkDelete: (ids) => axiosInstance.post('/persons/bulk-delete', { ids }),

  // Get person folder structure
  getFolders: (id) => axiosInstance.get(`/persons/${id}/folders`),

  // Get persons by location
  getByLocation: (locationId) => axiosInstance.get('/persons', {
    params: { personLocation: locationId }
  }),

  // Invite person to become a user
  sendInvite: (id, data) => axiosInstance.post(`/persons/${id}/invite`, data),

  // Resend invite email
  resendInvite: (id) => axiosInstance.post(`/persons/${id}/resend-invite`),

  // Cancel pending invite
  cancelInvite: (id) => axiosInstance.delete(`/persons/${id}/invite`),

  // Get linked user info
  getLinkedUser: (id) => axiosInstance.get(`/persons/${id}/user`),
};

// API client for user home buttons (per-person home screen config)
export const homeButtonsApi = {
  // Get buttons for a person (by linked userId)
  getByPerson: (personId) => axiosInstance.get(`/persons/${personId}/home-buttons`),

  // Save/replace all buttons for a person
  saveForPerson: (personId, buttons) => axiosInstance.put(`/persons/${personId}/home-buttons`, { buttons }),

  // Reset to default buttons
  resetForPerson: (personId) => axiosInstance.delete(`/persons/${personId}/home-buttons`),
};

// API client for person types
export const personTypesApi = {
  // Get all person types
  getAll: (params) => axiosInstance.get('/persontypes', { params }),

  // Get specific person type by ID
  getById: (id) => axiosInstance.get(`/persontypes/${id}`),

  // Create new person type
  create: (data) => axiosInstance.post('/persontypes', data),

  // Update existing person type
  update: (id, data) => axiosInstance.put(`/persontypes/${id}`, data),

  // Delete person type
  delete: (id) => axiosInstance.delete(`/persontypes/${id}`),

  // Bulk delete person types
  bulkDelete: (ids) => axiosInstance.post('/persontypes/bulk-delete', { ids }),
};

// API client for firmware versions
export const firmwareVersionsApi = {
  // Get all firmware versions with optional filters
  getAll: (params) => axiosInstance.get('/firmwareversions', { params }),

  // Get firmware version by ID
  getById: (id) => axiosInstance.get(`/firmwareversions/${id}`),

  // Get firmware versions by device type
  getByDeviceType: (deviceTypeId) => axiosInstance.get('/firmwareversions', {
    params: { device_type: deviceTypeId, limit: 1000 }
  }),

  // Create new firmware version
  create: (data) => axiosInstance.post('/firmwareversions', data),

  // Update firmware version
  update: (id, data) => axiosInstance.put(`/firmwareversions/${id}`, data),

  // Delete firmware version
  delete: (id) => axiosInstance.delete(`/firmwareversions/${id}`),

  // Bulk delete
  bulkDelete: (ids) => axiosInstance.post('/firmwareversions/bulk-delete', { ids }),
};

// API client for Emergency Service Integrations
export const emergencyServicesApi = {
  // Statistics
  getStats: () => axiosInstance.get('/emergency-services/stats'),

  // Providers
  getProviders: (params) => axiosInstance.get('/emergency-services/providers', { params }),
  getProviderById: (id) => axiosInstance.get(`/emergency-services/providers/${id}`),
  createProvider: (data) => axiosInstance.post('/emergency-services/providers', data),
  updateProvider: (id, data) => axiosInstance.put(`/emergency-services/providers/${id}`, data),
  deleteProvider: (id) => axiosInstance.delete(`/emergency-services/providers/${id}`),

  // Integrations
  getIntegrations: (params) => axiosInstance.get('/emergency-services/integrations', { params }),
  getIntegrationById: (id) => axiosInstance.get(`/emergency-services/integrations/${id}`),
  getIntegrationsByLocation: (locationId) => axiosInstance.get(`/emergency-services/location/${locationId}/integrations`),
  createIntegration: (data) => axiosInstance.post('/emergency-services/integrations', data),
  updateIntegration: (id, data) => axiosInstance.put(`/emergency-services/integrations/${id}`, data),
  deleteIntegration: (id) => axiosInstance.delete(`/emergency-services/integrations/${id}`),
  testIntegration: (id) => axiosInstance.post(`/emergency-services/integrations/${id}/test`),

  // Alarm Types (for mapping)
  getAlarmTypes: () => axiosInstance.get('/emergency-services/alarm-types'),

  // Available Sites (for integration dialog)
  getSites: (params) => axiosInstance.get('/emergency-services/sites', { params }),
};

// API client for Alarms
export const alarmsApi = {
  // Active alarms
  getActiveAlarms: () => axiosInstance.get('/alarms/active'),

  // Alarm history
  getAlarmHistory: (params) => axiosInstance.get('/alarms/history', { params }),

  // Get alarm by ID (authenticated)
  getAlarmById: (id) => axiosInstance.get(`/alarms/${id}`),

  // Get alarm by UUID (public - no auth required)
  // Uses publicAxiosInstance to avoid auth redirects
  // Only works for non-terminal alarms (between countdown and finished)
  getAlarmByUUID: (uuid) => publicAxiosInstance.get(`/alarms/uuid/${uuid}`),

  // Update alarm status (authenticated)
  // comment is required for terminal states (finished, cancelled)
  updateAlarmStatus: (id, status, comment = null) => axiosInstance.post(`/alarms/${id}/status`, { status, comment }),

  // Update alarm status by UUID (public - no auth required)
  // Uses publicAxiosInstance to avoid auth redirects
  // Only works for non-terminal alarms
  // comment is required for terminal states (finished, cancelled)
  updateAlarmStatusByUUID: (uuid, status, comment = null) => publicAxiosInstance.post(`/alarms/uuid/${uuid}/status`, { status, comment }),

  // CRUD operations
  getAll: (params) => axiosInstance.get('/alarms', { params }),
  create: (data) => axiosInstance.post('/alarms', data),
  update: (id, data) => axiosInstance.put(`/alarms/${id}`, data),
  delete: (id) => axiosInstance.delete(`/alarms/${id}`),
};

// API client for button tests
export const buttonTestsApi = {
  // Start a new test session
  startTest: (data) => axiosInstance.post('/button-tests/start', data),

  // Get test session status
  getTestStatus: (testUUID) => axiosInstance.get(`/button-tests/${testUUID}`),

  // End a test session
  endTest: (testUUID, data) => axiosInstance.post(`/button-tests/${testUUID}/end`, data),

  // Skip a button in the test (reason is mandatory, min 3 characters)
  skipButton: (testUUID, resultId, data) => axiosInstance.post(`/button-tests/${testUUID}/skip/${resultId}`, data),

  // Update button name during test
  updateButtonName: (testUUID, resultId, friendlyName) =>
    axiosInstance.put(`/button-tests/${testUUID}/button/${resultId}/name`, { friendlyName }),

  // Update button room (for fixed buttons that must be linked to a room)
  updateButtonRoom: (testUUID, resultId, roomId) =>
    axiosInstance.put(`/button-tests/${testUUID}/button/${resultId}/room`, { roomId }),

  // Get naming suggestions (rooms and persons)
  getNamingSuggestions: (testUUID) => axiosInstance.get(`/button-tests/${testUUID}/naming-suggestions`),

  // Get test history for a location
  getTestHistory: (locationId, params) => axiosInstance.get(`/button-tests/history/${locationId}`, { params }),

  // Get test report for download
  getTestReport: (testUUID, format = 'json') => {
    const config = format === 'csv'
      ? { params: { format: 'csv' }, responseType: 'text' }
      : { params: { format: 'json' } };
    return axiosInstance.get(`/button-tests/${testUUID}/report`, config);
  },
};

// API client for update paths
export const updatePathsApi = {
  // Get all update paths with optional filters
  getAll: (params) => axiosInstance.get('/updatepaths', { params }),

  // Get update path by ID
  getById: (id) => axiosInstance.get(`/updatepaths/${id}`),

  // Get update paths by device type
  getByDeviceType: (deviceTypeId) => axiosInstance.get('/updatepaths', {
    params: { update_path_device_type: deviceTypeId, limit: 1000 }
  }),

  // Create new update path
  create: (data) => axiosInstance.post('/updatepaths', data),

  // Update update path
  update: (id, data) => axiosInstance.put(`/updatepaths/${id}`, data),

  // Delete update path
  delete: (id) => axiosInstance.delete(`/updatepaths/${id}`),

  // Bulk delete
  bulkDelete: (ids) => axiosInstance.post('/updatepaths/bulk-delete', { ids }),
};

// API client for services
export const servicesApi = {
  // Get all services with optional filters
  getAll: (params) => axiosInstance.get('/services', { params }),

  // Get service by ID
  getById: (id) => axiosInstance.get(`/services/${id}`),

  // Create new service
  create: (data) => axiosInstance.post('/services', data),

  // Update service
  update: (id, data) => axiosInstance.put(`/services/${id}`, data),

  // Delete service
  delete: (id) => axiosInstance.delete(`/services/${id}`),

  // Versions
  getVersions: (serviceId) => axiosInstance.get(`/services/${serviceId}/versions`),
  createVersion: (serviceId, data) => axiosInstance.post(`/services/${serviceId}/versions`, data),
  updateVersion: (id, data) => axiosInstance.put(`/services/versions/${id}`, data),
  deleteVersion: (id) => axiosInstance.delete(`/services/versions/${id}`),

  // Update Paths
  getUpdatePaths: (serviceId) => axiosInstance.get(`/services/${serviceId}/update-paths`),
  createUpdatePath: (serviceId, data) => axiosInstance.post(`/services/${serviceId}/update-paths`, data),
  updateUpdatePath: (id, data) => axiosInstance.put(`/services/update-paths/${id}`, data),
  deleteUpdatePath: (id) => axiosInstance.delete(`/services/update-paths/${id}`),
};

// API client for device types
export const deviceTypesApi = {
  // Get all device types with optional filters
  getAll: (params) => axiosInstance.get('/deviceTypes', { params }),

  // Get device type by ID
  getById: (id) => axiosInstance.get(`/deviceTypes/${id}`),

  // Create new device type
  create: (data) => axiosInstance.post('/deviceTypes', data),

  // Update device type
  update: (id, data) => axiosInstance.put(`/deviceTypes/${id}`, data),

  // Delete device type
  delete: (id) => axiosInstance.delete(`/deviceTypes/${id}`),

  // Get services for a device type
  getServices: (id) => axiosInstance.get(`/deviceTypes/${id}/services`),

  // Update services for a device type
  updateServices: (id, data) => axiosInstance.put(`/deviceTypes/${id}/services`, data),
};

// API client for Qbus domotics integration
export const qbusApi = {
  // Output Types
  getOutputTypes: () => axiosInstance.get('/qbus/output-types'),

  // Controllers
  getControllersForLocation: (locationId) => axiosInstance.get(`/qbus/locations/${locationId}/controllers`),

  // Outputs
  getOutputsForController: (controllerId) => axiosInstance.get(`/qbus/controllers/${controllerId}/outputs`),
  getOutputsForLocation: (locationId, params) => axiosInstance.get(`/qbus/locations/${locationId}/outputs`, { params }),
  updateOutput: (outputId, data) => axiosInstance.patch(`/qbus/outputs/${outputId}`, data),
  bulkAssignOutputType: (outputIds, outputTypeId) => axiosInstance.patch('/qbus/outputs/bulk-assign-type', { outputIds, outputTypeId }),
  getUniqueOutputNames: (locationId) => axiosInstance.get(`/qbus/locations/${locationId}/unique-names`),
  bulkUpdateFriendlyNames: (locationId, mappings) => axiosInstance.patch('/qbus/outputs/bulk-friendly-names', { locationId, mappings }),
  getUniqueOutputNamesGlobal: () => axiosInstance.get('/qbus/unique-names'),
  bulkUpdateFriendlyNamesGlobal: (mappings) => axiosInstance.patch('/qbus/outputs/bulk-friendly-names-global', { mappings }),

  // Output Commands (fire-and-forget)
  sendOutputCommand: (outputId, value) => axiosInstance.post(`/qbus/outputs/${outputId}/command`, { value }),

  // Output History
  getOutputHistory: (outputId, params) => axiosInstance.get(`/qbus/outputs/${outputId}/history`, { params }),

  // Hub Management
  refreshHubConfig: (hubId) => axiosInstance.post(`/qbus/hubs/${hubId}/refresh-config`),
  getHubStatus: (hubId) => axiosInstance.get(`/qbus/hubs/${hubId}/status`),

  // Icon Mappings
  getIconMappings: () => axiosInstance.get('/qbus/icon-mappings'),
  createIconMapping: (data) => axiosInstance.post('/qbus/icon-mappings', data),
  updateIconMapping: (id, data) => axiosInstance.put(`/qbus/icon-mappings/${id}`, data),
  deleteIconMapping: (id) => axiosInstance.delete(`/qbus/icon-mappings/${id}`),

  // Folders
  getFolders: (locationId) => axiosInstance.get(`/qbus/locations/${locationId}/folders`),
  createFolder: (locationId, data) => axiosInstance.post(`/qbus/locations/${locationId}/folders`, data),
  updateFolder: (folderId, data) => axiosInstance.patch(`/qbus/folders/${folderId}`, data),
  deleteFolder: (folderId) => axiosInstance.delete(`/qbus/folders/${folderId}`),
  reorderFolders: (locationId, folderIds) => axiosInstance.put(`/qbus/locations/${locationId}/folders/reorder`, { folderIds }),
  assignOutputsToFolder: (outputIds, folderId) => axiosInstance.patch('/qbus/outputs/assign-folder', { outputIds, folderId }),

  // Automations
  getAutomations: (locationId, params) => axiosInstance.get(`/qbusAutomations/locations/${locationId}`, { params }),
  getSystemAutomations: (params) => axiosInstance.get('/qbusAutomations/system', { params }),
  getAutomation: (id) => axiosInstance.get(`/qbusAutomations/${id}`),
  createAutomation: (locationId, data) => axiosInstance.post(`/qbusAutomations/locations/${locationId}`, data),
  createSystemAutomation: (data) => axiosInstance.post('/qbusAutomations/system', data),
  updateAutomation: (id, data) => axiosInstance.put(`/qbusAutomations/${id}`, data),
  deleteAutomation: (id) => axiosInstance.delete(`/qbusAutomations/${id}`),
  toggleAutomation: (id) => axiosInstance.patch(`/qbusAutomations/${id}/toggle`),
  testAutomation: (id, triggerData) => axiosInstance.post(`/qbusAutomations/${id}/test`, { triggerData }),
  getAutomationExecutions: (id, params) => axiosInstance.get(`/qbusAutomations/${id}/executions`, { params }),
  getExecutionDetail: (executionId) => axiosInstance.get(`/qbusAutomations/executions/${executionId}`),
  cancelExecution: (executionId) => axiosInstance.post(`/qbusAutomations/executions/${executionId}/cancel`),
  getBlockTypes: () => axiosInstance.get('/qbusAutomations/block-types'),
  replayExecution: (automationId, executionId) => axiosInstance.post(`/qbusAutomations/${automationId}/replay/${executionId}`),
};

// API client for family photo links (Tier 1 — magic link photo sharing)
export const familyLinksApi = {
  getAll: (params) => axiosInstance.get('/family-links', { params }),
  create: (data) => axiosInstance.post('/family-links', data),
  deactivate: (id) => axiosInstance.delete(`/family-links/${id}`),
  reactivate: (id) => axiosInstance.post(`/family-links/${id}/reactivate`),
  getPhotos: (linkId, params) => axiosInstance.get(`/family-links/${linkId}/photos`, { params }),
};

// API client for resident events (activities, appointments, agenda)
export const residentEventsApi = {
  getAll: (params) => axiosInstance.get('/resident-events', { params }),
  create: (data) => axiosInstance.post('/resident-events', data),
  update: (id, data) => axiosInstance.put(`/resident-events/${id}`, data),
  delete: (id) => axiosInstance.delete(`/resident-events/${id}`),
  getSignups: (id) => axiosInstance.get(`/resident-events/${id}/signups`),
};

export const einkDisplayApi = {
  // Template management (portal designer — keyed by device type ID)
  getAll: () => axiosInstance.get('/einkDisplays/templates'),
  getByDeviceType: (deviceTypeId) => axiosInstance.get(`/einkDisplays/templates/${deviceTypeId}`),
  saveTemplate: (deviceTypeId, data) => axiosInstance.post(`/einkDisplays/templates/${deviceTypeId}`, data),
  deleteTemplate: (deviceTypeId) => axiosInstance.delete(`/einkDisplays/templates/${deviceTypeId}`),
  push: (deviceTypeId) => axiosInstance.post(`/einkDisplays/templates/${deviceTypeId}/push`),
  // Device polling (kept for device scripts — keyed by device ID)
  getState: (deviceId, since, varSince) => axiosInstance.get(`/einkDisplays/${deviceId}/state`, { params: { since, varSince } }),
  updateVariables: (deviceId, variables) => axiosInstance.post(`/einkDisplays/${deviceId}/variables`, variables),
  sendButton: (deviceId, button) => axiosInstance.post(`/einkDisplays/${deviceId}/button`, { button }),
};

// Export the axios instance for direct use by other services
export { axiosInstance };

// Export default API client
const apiClient = {
  auth: authApi,
  tickets: ticketsApi,
  locations: locationsApi,
  devices: devicesApi,
  notifications: notificationsApi,
  users: usersApi,
  logs: logsApi,
  cameraFootageRequests: cameraFootageRequestsApi,
  companies: companiesApi,
  fieldInterventions: fieldInterventionsApi,
  documentation: documentationApi,
  files: filesApi,
  hexnode: hexnodeApi,
  unifi: unifiApi,
  clearvox: clearvoxApi,
  baseIP: baseIPApi,
  persons: personsApi,
  personTypes: personTypesApi,
  firmwareVersions: firmwareVersionsApi,
  updatePaths: updatePathsApi,
  emergencyServices: emergencyServicesApi,
  alarms: alarmsApi,
  buttonTests: buttonTestsApi,
  services: servicesApi,
  deviceTypes: deviceTypesApi,
  qbus: qbusApi,
  telephony: telephonyApi,
  familyLinks: familyLinksApi,
  residentEvents: residentEventsApi,
  einkDisplay: einkDisplayApi
};

export default apiClient;

// Export twonApi separately since it's in its own service file
export { twonApi } from './twonService';